import type { InboxFullMessageFragment } from "@qw/graphql";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  type MaterialSymbol,
  sanitizeMessageHtml,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import parse, { type DOMNode, domToReact, Element } from "html-react-parser";
import type { FC } from "react";

import { MessageHtml, type MessageHtmlProps } from "./message-html";

export interface MessageBodyProps {
  message: InboxFullMessageFragment;
}

/**
 * Transforms .qwib-signature and .qwib-quote divs into Collapsible components.
 *
 * Both start closed on every message, the oldest included: with the whole thread on screen, a quote
 * only repeats what the reader already has above it. Forwards are the exception and stay open —
 * see below.
 */
const parseOptions = {
  replace: (domNode: DOMNode) => {
    if (!(domNode instanceof Element)) {
      return;
    }
    if (!domNode.attribs?.class) {
      return;
    }

    const classes = domNode.attribs.class;
    const isSignature = classes.includes("qwib-signature");
    // A forward is marked up as a quote but is not one. A quoted reply repeats what the reader
    // already has above it; a forward's content appears nowhere else in the thread, so folding it
    // hides the only copy — on a message that is nothing but a forward, the whole body. It stays
    // body content.
    const isForward = classes.includes("qwib-quote--forward");
    const isQuote = !isForward && classes.includes("qwib-quote");

    if (!isSignature && !isQuote) {
      return;
    }

    const getTriggerIcon = (): MaterialSymbol => {
      if (isSignature) {
        return "signature";
      }

      return "more_horiz";
    };

    const getTooltipText = (): string => {
      if (isSignature) {
        return "Show signature";
      }

      return "Show quoted message";
    };

    const triggerIcon = getTriggerIcon();
    const tooltipText = getTooltipText();

    return (
      <Collapsible className="mt-3">
        <Tooltip>
          <CollapsibleTrigger asChild>
            <TooltipTrigger asChild>
              <Button variant="secondary" size="sm" className="rounded-full py-0 px-3 h-5">
                <Icon icon={triggerIcon} />
              </Button>
            </TooltipTrigger>
          </CollapsibleTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>
        <CollapsibleContent className="pt-3">{domToReact(domNode.children as DOMNode[])}</CollapsibleContent>
      </Collapsible>
    );
  },
};

export const MessageBody: FC<MessageBodyProps> = ({ message }) => {
  const { html, text, annotatedHtml } = message;

  // Priority 1: Use annotatedHtml if available
  if (annotatedHtml) {
    const sanitized = sanitizeMessageHtml(annotatedHtml);
    const parsed = parse(sanitized, parseOptions);

    return <MessageHtmlContent html={parsed} />;
  }

  // Priority 2: Fall back to plain HTML
  if (html) {
    const parsed = parse(sanitizeMessageHtml(html));
    return <MessageHtmlContent html={parsed} />;
  }

  // Priority 3: Fall back to plain text
  if (text) {
    return <MessageHtmlContent text={text} />;
  }

  return <div className="py-6 italic text-muted-foreground">No content available for this message</div>;
};

const MessageHtmlContent: FC<MessageHtmlProps> = (props) => (
  <MessageHtml {...props} className="dark:bg-white dark:py-6 dark:px-10 dark:-mx-10" />
);
